// meta-orderings
/**
 * Returns an ordering that applies to an array of the original type
 *
 * Compares items in order, returning on difference
 *
 * If the end of an array is reached before the other, the shorter array compares first
 */
export function arrayOrd(ord) {
    return (a, b) => {
        for (let i = 0; i < Math.min(a.length, b.length); i++) {
            // eslint-disable-next-line security/detect-object-injection
            const s = ord(a[i], b[i]);
            if (s !== 0)
                return s;
        }
        return a.length - b.length;
    };
}
export function tieredOrd(...initOrds) {
    const ords = initOrds.flat();
    return (a, b) => {
        for (const ord of ords) {
            const val = ord(a, b);
            if (val !== 0)
                return val;
        }
        return 0;
    };
}
/**
 * Returns an ordering that operates on a mapped version of the ordered type
 */
export function mapOrd(ord, map) {
    return (a, b) => ord(map(a), map(b));
}
/**
 * Returns an ordering that is the opposite of the supplied one
 */
export function reverseOrd(ord) {
    return (a, b) => -ord(a, b);
}
/**
 * Returns an ordering that has the nulls first, then the supplied ordering
 */
export function nullsFirst(ord) {
    return (a, b) => {
        const aN = a == null;
        const bN = b == null;
        if (aN !== bN) {
            return aN ? -1 : 1;
        }
        if (aN)
            return 0;
        return ord(a, b);
    };
}
/**
 * Returns an ordering that has the nulls last, after the supplied ordering
 */
export function nullsLast(ord) {
    return (a, b) => {
        const aN = a == null;
        const bN = b == null;
        if (aN !== bN) {
            return aN ? 1 : -1;
        }
        if (aN)
            return 0;
        return ord(a, b);
    };
}
// generic orderings
/**
 * Ordering based on the === and \< operators
 */
export const rawOrd = (a, b) => {
    if (a === b)
        return 0;
    else if (a < b)
        return -1;
    else
        return 1;
};
/**
 * Ordering based on the localeCompare function for strings
 */
export const localeOrd = (a, b) => {
    return a.localeCompare(b);
};
/**
 * Numeric ordering
 */
export const numericOrd = (a, b) => {
    return +a - +b;
};
/**
 * Compound number/text ordering by JS type
 *
 * Uses {@link localeOrd} for strings and {@link numericOrd} for numbers
 */
export const compoundOrd = (a, b) => {
    const aStr = +(typeof a == 'string');
    const bStr = +(typeof b == 'string');
    if (aStr !== bStr)
        return aStr - bStr;
    if (aStr)
        return localeOrd(a, b);
    else
        return numericOrd(a, b);
};
/**
 * Compound number/text ordering by "numberable" (numbers or strings that parse as numbers)
 *
 * Uses {@link localeOrd} for non-numerables and {@link numericOrd} for numerables
 */
export const compoundOrd2 = (a, b) => {
    const aStr = +isNaN(+a);
    const bStr = +isNaN(+b);
    if (aStr !== bStr)
        return aStr - bStr;
    if (aStr)
        return localeOrd('' + a, '' + b);
    else
        return numericOrd(+a, +b);
};
/**
 * "Natural" ordering: a1\<a2\<a11\<a12\<a21\<a22\<null
 */
export const naturalOrd = nullsLast(mapOrd(arrayOrd(compoundOrd2), e => e.match(/[0-9]+|[^0-9]+/g) ?? ['']));
/**
 * Keyed ordering, same order as the argument
 */
export function keyedOrd(keys, restId = Infinity) {
    const indexes = new Map();
    if (Array.isArray(keys)) {
        // eslint-disable-next-line security/detect-object-injection
        for (let i = 0; i < keys.length; i++)
            indexes.set(keys[i], i);
    }
    else {
        for (const [k, v] of keys.entries())
            indexes.set(k, v);
    }
    return mapOrd(rawOrd, x => indexes.get(x) ?? restId);
}
//# sourceMappingURL=data:application/json;base64,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